import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  formatDayLabel,
  getScheduleDate,
  getSlotBlockReason,
} from '~/pages/Marketing/AppOnlyFlash/appOnlyFlashCalendar';

import { APP_EXCLUSIVE_BRAND, APP_EXCLUSIVE_SCHEDULE_TYPE } from '~/consts/appExclusive';

import OffersService, { ScheduleRecord } from '~/services/OffersService';

import { appExclusiveScheduleQueryKeys } from './useGetAppExclusiveSchedules';
import { fetchAppExclusiveSlotCheck } from './useGetAppExclusiveSlotCheck';
import { offerQueryKeys } from './useGetOffer';

interface Slot {
  start: string;
  end: string;
}

export interface MoveAppExclusiveScheduleInput {
  schedule: ScheduleRecord;
  /** Offer name for the eligible lookup's name search; null when the offer isn't loaded. */
  searchName: string | null;
  /** Sydney calendar day to move to, YYYY-MM-DD. */
  targetDate: string;
  /** Deal holding the target day; it takes the moving deal's old day. */
  swapWith?: { schedule: ScheduleRecord; offerName: string; searchName: string | null; targetDate: string };
}

/**
 * Re-runs the eligible lookup for `offerId` on `date` right before writing and returns the fresh slot.
 * The dialog's check can be minutes old, and svc-offer only guards the day clash on write, not the
 * on-sale window.
 */
async function checkSlot(
  offerId: string,
  region: string,
  date: string,
  searchName: string | null,
  ignoreScheduleIds: Array<number>,
): Promise<Slot> {
  const eligible = await fetchAppExclusiveSlotCheck({ region, date, offerId, offerName: searchName });
  const reason = getSlotBlockReason(eligible, offerId, ignoreScheduleIds);
  if (reason) {
    throw new Error(`${formatDayLabel(date)} no longer fits: ${reason}`);
  }
  return { start: eligible.slot.start, end: eligible.slot.end };
}

function createAppExclusive(offerId: string, region: string, { start, end }: Slot) {
  return OffersService.createSchedule(
    { type: APP_EXCLUSIVE_SCHEDULE_TYPE, brand: APP_EXCLUSIVE_BRAND, region, start, end },
    offerId,
  );
}

/**
 * Moves an App Only Flash row to another day, optionally swapping with the row on that day.
 * svc-offer rejects two offers overlapping in a region and has no batch write, so a swap removes the
 * other row, moves this one, then re-adds the other on the freed day. A failed move puts the other
 * row back where it was; if that also fails, the error names the removed deal so it can be re-added.
 * These three writes aren't atomic: a single swap endpoint in svc-offer would remove the gaps.
 */
async function moveAppExclusive({ schedule, searchName, targetDate, swapWith }: MoveAppExclusiveScheduleInput) {
  const offerId = schedule.fk_offer_salesforce_id;
  // Both rows are moving, so neither blocks the other's new day. Checked before any write.
  const ignore = swapWith ? [schedule.id, swapWith.schedule.id] : [schedule.id];
  const target = await checkSlot(offerId, schedule.region, targetDate, searchName, ignore);
  const swapTarget = swapWith
    ? await checkSlot(
        swapWith.schedule.fk_offer_salesforce_id,
        swapWith.schedule.region,
        swapWith.targetDate,
        swapWith.searchName,
        ignore,
      )
    : null;

  if (!swapWith) {
    await OffersService.updateSchedule(target, offerId, schedule.id);
    return;
  }

  const other = swapWith.schedule;
  await OffersService.deleteSchedule(other.fk_offer_salesforce_id, other.id);
  try {
    await OffersService.updateSchedule(target, offerId, schedule.id);
  } catch (moveErr) {
    try {
      await createAppExclusive(other.fk_offer_salesforce_id, other.region, other);
    } catch (restoreErr) {
      // The other row is already deleted: the admin must know it's gone, or its day stays empty.
      throw new Error(
        `${moveErr?.message ?? 'unknown error'}. ${swapWith.offerName} was removed for the swap and could not be put back on ${formatDayLabel(getScheduleDate(other))} (${restoreErr?.message ?? 'unknown error'}). Add it again from the calendar.`,
        { cause: restoreErr },
      );
    }
    throw moveErr;
  }
  try {
    await createAppExclusive(other.fk_offer_salesforce_id, other.region, swapTarget);
  } catch (err) {
    throw new Error(
      `the deal moved, but ${swapWith.offerName} could not be re-added (${err?.message ?? 'unknown error'}). Add it again from the calendar.`,
      { cause: err },
    );
  }
}

export function useMoveAppExclusiveSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: moveAppExclusive,
    // Invalidate on failure too: a partial swap still changes the calendar.
    onSettled: (_data, _error, { schedule, swapWith }) => {
      queryClient.invalidateQueries({ queryKey: appExclusiveScheduleQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(schedule.fk_offer_salesforce_id) });
      if (swapWith) {
        queryClient.invalidateQueries({
          queryKey: offerQueryKeys.detail(swapWith.schedule.fk_offer_salesforce_id),
        });
      }
    },
  });
}
